/**
 * Where a support message's pictures sit in its text.
 *
 * A line that reads exactly `[picture N]` places the message's Nth picture
 * there; a picture no line names follows the text. Plain text on purpose:
 * every reader that does not draw pictures — the AI thread digest, a mail
 * client quoting the reply — still shows what was there. The backend reads the
 * same rule in `core/logic/support/picture_tokens.py`.
 */

const PICTURE_TOKEN = /^\[picture (\d+)\]$/;

export const pictureToken = (n: number): string => `[picture ${n}]`;

/** The picture number a line places, or null for a line of text. */
export const pictureNumber = (line: string): number | null => {
	const match = line.trim().match(PICTURE_TOKEN);
	return match ? Number(match[1]) : null;
};

/**
 * Whether anything was written besides pictures. A picture puts its own
 * `[picture N]` line in the text, so a body holding only those is a message
 * nobody has written yet: it neither enables Send nor survives closing.
 */
export const hasWrittenText = (body: string): boolean =>
	body.split("\n").some((line) => line.trim() !== "" && pictureNumber(line) === null);

export type BodyPart =
	| { kind: "text"; text: string }
	| { kind: "picture"; number: number };

export const splitPictureTokens = (body: string): BodyPart[] => {
	const parts: BodyPart[] = [];
	let lines: string[] = [];
	const flush = (): void => {
		const text = lines.join("\n").trim();
		if (text) parts.push({ kind: "text", text });
		lines = [];
	};
	for (const line of body.split("\n")) {
		const number = pictureNumber(line);
		if (number !== null) {
			flush();
			parts.push({ kind: "picture", number });
		} else {
			lines.push(line);
		}
	}
	flush();
	return parts;
};

/** `text` with picture `number`'s line put at `cursor`, on a line of its own. */
export const insertPictureToken = (
	text: string,
	cursor: number,
	number: number
): string => {
	const before = text.slice(0, cursor);
	const after = text.slice(cursor);
	const lead = before && !before.endsWith("\n") ? "\n" : "";
	const trail = after.startsWith("\n") ? "" : "\n";
	return `${before}${lead}${pictureToken(number)}${trail}${after}`;
};

/**
 * `text` without picture `number`'s line, and every later picture's line
 * renumbered one down — the pictures after it each moved up one place.
 */
export const removePictureToken = (text: string, number: number): string =>
	text
		.split("\n")
		.filter((line) => line.trim() !== pictureToken(number))
		.map((line) => {
			const n = pictureNumber(line) ?? 0;
			return n > number ? pictureToken(n - 1) : line;
		})
		.join("\n");
